import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const states = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const PublicApplication = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    applicant_type: 'borrower',
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    street_address: '',
    street_address_2: '',
    city: '',
    state: '',
    zip_code: '',
    loan_type: 'purchase',
    loan_amount: '',
    credit_score: '',
    property_info: '',
    has_credit_report: 'no',
    other_docs: '',
    referral_source: '',
    investment_areas: [],
    message: ''
  });

  const handleCheckboxChange = (state, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        investment_areas: [...prev.investment_areas, state]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        investment_areas: prev.investment_areas.filter(s => s !== state)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/applications`, {
        borrower_name: `${formData.first_name} ${formData.last_name}`,
        email: formData.email,
        phone: formData.phone,
        property_address: `${formData.street_address}, ${formData.city}, ${formData.state} ${formData.zip_code}`,
        loan_purpose: formData.loan_type,
        loan_amount: parseFloat(formData.loan_amount) || 0,
        property_value: 0,
        credit_score: formData.credit_score,
        application_data: formData
      });

      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-primary">Application Submitted!</h1>
          <p className="text-muted-foreground">
            Thank you for your application. We'll review it and get back to you shortly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary mb-2">Apply for a Loan</h1>
          <p className="text-muted-foreground">Complete the form below to start your application</p>
        </div>

        <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* I am a */}
            <div className="space-y-2">
              <Label htmlFor="applicant_type">I am a *</Label>
              <Select 
                value={formData.applicant_type} 
                onValueChange={(value) => setFormData({ ...formData, applicant_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="borrower">Borrower</SelectItem>
                  <SelectItem value="broker">Broker</SelectItem>
                  <SelectItem value="realtor">Realtor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Full Name */}
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="First Name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
                <Input
                  placeholder="Last Name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Company */}
            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="(000) 000-0000"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </div>

            {/* Subject Property Address */}
            <div className="space-y-2">
              <Label>Subject Property Address *</Label>
              <Input
                placeholder="Street Address"
                value={formData.street_address}
                onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                required
              />
              <Input
                placeholder="Street Address Line 2"
                value={formData.street_address_2}
                onChange={(e) => setFormData({ ...formData, street_address_2: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="City"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
                <Select 
                  value={formData.state} 
                  onValueChange={(value) => setFormData({ ...formData, state: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="State / Province" />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                placeholder="Postal / Zip Code"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                required
              />
            </div>

            {/* Loan Request Type */}
            <div className="space-y-2">
              <Label>Loan Request Type *</Label>
              <RadioGroup 
                value={formData.loan_type} 
                onValueChange={(value) => setFormData({ ...formData, loan_type: value })}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="purchase" id="purchase" />
                  <Label htmlFor="purchase" className="font-normal">Purchase</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="refinance" id="refinance" />
                  <Label htmlFor="refinance" className="font-normal">Refinance</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Desired Loan Amount */}
            <div className="space-y-2">
              <Label htmlFor="loan_amount">Desired Loan Amount *</Label>
              <Input
                id="loan_amount"
                type="number"
                placeholder="$"
                value={formData.loan_amount}
                onChange={(e) => setFormData({ ...formData, loan_amount: e.target.value })}
                required
              />
            </div>

            {/* Credit Score */}
            <div className="space-y-2">
              <Label htmlFor="credit_score">Approximate Credit Score *</Label>
              <Input
                id="credit_score"
                placeholder="(no minimum score requirement)"
                value={formData.credit_score}
                onChange={(e) => setFormData({ ...formData, credit_score: e.target.value })}
                required
              />
            </div>

            {/* Property Information */}
            <div className="space-y-2">
              <Label htmlFor="property_info">Property Information (i.e. Bed/Bath, Square Footage, Existing Liens, etc...)</Label>
              <Textarea
                id="property_info"
                value={formData.property_info}
                onChange={(e) => setFormData({ ...formData, property_info: e.target.value })}
                rows={3}
              />
            </div>

            {/* Credit Report */}
            <div className="space-y-2">
              <Label htmlFor="has_credit_report">Do you have a copy of a recent credit report?</Label>
              <Select 
                value={formData.has_credit_report} 
                onValueChange={(value) => setFormData({ ...formData, has_credit_report: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Other Docs */}
            <div className="space-y-2">
              <Label htmlFor="other_docs">Other Relevant Docs (i.e. Purchase Contract, Addenda, etc...)</Label>
              <Input
                id="other_docs"
                placeholder="Upload files or provide links"
                value={formData.other_docs}
                onChange={(e) => setFormData({ ...formData, other_docs: e.target.value })}
              />
            </div>

            {/* Referral Source */}
            <div className="space-y-2">
              <Label htmlFor="referral_source">How did you hear about Axe Capital Lending? *</Label>
              <Select 
                value={formData.referral_source} 
                onValueChange={(value) => setFormData({ ...formData, referral_source: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Please Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="google">Google Search</SelectItem>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="social">Social Media</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Investment Areas */}
            <div className="space-y-2">
              <Label>Investment Areas of Interest (select all that apply) *</Label>
              <div className="grid grid-cols-3 gap-3">
                {states.map(state => (
                  <div key={state} className="flex items-center space-x-2">
                    <Checkbox
                      id={state}
                      checked={formData.investment_areas.includes(state)}
                      onCheckedChange={(checked) => handleCheckboxChange(state, checked)}
                    />
                    <Label htmlFor={state} className="font-normal text-sm">{state}</Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
                required
              />
            </div>

            {/* Submit Button */}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PublicApplication;
